import { DialogContent } from "@mui/material";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import {
  BottomSheet,
  LoadingSpinner,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useEffect, useRef } from "react";

export interface UpdateExternalAccountBottomSheetProps {
  modalState: UseModalState;
  isGoToStripeLoading: boolean;
  onClose: () => void;
  onGoToStripe: () => void;
}

export function UpdateExternalAccountBottomSheet(props: UpdateExternalAccountBottomSheetProps) {
  const { modalState, isGoToStripeLoading, onClose, onGoToStripe } = props;
  const didGoToStripe = useRef(false);

  useEffect(() => {
    if (modalState.modalIsClosed) {
      didGoToStripe.current = false;
    }
  }, [modalState.modalIsClosed]);

  useEffect(() => {
    // user clicked to go to stripe, and loading finished
    // -> auto-close sheet
    if (!isGoToStripeLoading && didGoToStripe.current) {
      modalState.closeModal();
    }
  }, [isGoToStripeLoading, modalState]);

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={onClose} />}
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            sx={(theme) => ({ marginBottom: theme.spacing(2) })}
            disabled={isGoToStripeLoading}
            onClick={() => {
              didGoToStripe.current = true;
              onGoToStripe();
            }}
          >
            {isGoToStripeLoading ? <LoadingSpinner size="small" /> : "Take me to Stripe"}
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent sx={(theme) => ({ paddingX: theme.spacing(5) })}>
        <Text variant="h3" sx={(theme) => ({ marginTop: theme.spacing(3), textAlign: "center" })}>
          Need to update your payment destinations?
        </Text>
        <Text
          variant="body1"
          sx={(theme) => ({ marginTop: theme.spacing(9), textAlign: "center" })}
        >
          Update your payout details in Stripe:
        </Text>
        <Text
          variant="body1"
          sx={(theme) => ({ marginY: theme.spacing(5), textAlign: "center", fontWeight: 500 })}
        >
          ☰ Menu → Settings → Payout Details (at bottom)
        </Text>
        <Text
          variant="body2"
          sx={(theme) => ({ marginTop: theme.spacing(5), textAlign: "center" })}
        >
          💡 Tip: Add your new payout method first — then set it as default — before removing your
          old one.
        </Text>
      </DialogContent>
    </BottomSheet>
  );
}
